// libs
import React, { ReactElement, useMemo } from "react";
import cx from "classnames";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// utils
import { AriaHidden, format } from "@swiggy-private/core-ui";

// icons
import { PlusIcon, MinusIcon } from "@cart-ui/components/food-item/icons";

// constants
import { CART_ITEMS_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { QTY_CHANGE_ACTION_TYPE } from "../constants";

import styles from "../styles/FoodItem.scss";

import { isPopFlow, isToingFlow, getBridgeSdk } from "@swiggy-private/checkout-core";
import { VibrateAmplitudeTypes } from "@swiggy-private/native-bridge";
import { AddButton as AddButtonV2, ADD_BUTTON_VARIANT } from "./add-button-dls";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

export interface AddButtonProps {
    className?: string;
    onQuantityChange: (action: QTY_CHANGE_ACTION_TYPE) => void;
    quantity: number;
    updating: boolean;
    condensed?: boolean;
    disableIncrement?: boolean;
    customBorderColor?: keyof IColors;
    onMaxQuantityReached?: VoidFunction;
}

const ACCESSIBLITY_TEXTS = {
    ADD_BUTTON: "Increase Quantity to {0}",
    MINUS_BUTTON: "Decrease Quantity to {0}",
};

export const AddButtonV1 = React.memo(function AddButtonV1(props: AddButtonProps): ReactElement {
    const { onQuantityChange, className, condensed, disableIncrement, onMaxQuantityReached } = props;
    const isPopCart = isPopFlow();
    const plusIconColor = isPopCart ? "text_Highest_Emphasis" : "positive";

    const onDecQuantity = React.useCallback(() => {
        onQuantityChange(QTY_CHANGE_ACTION_TYPE.DECREMENT);
    }, [onQuantityChange]);

    const onIncQuantity = React.useCallback(() => {
        // Only trigger haptic feedback and shake when maximum quantity is reached
        if (disableIncrement) {
            // Haptic feedback - only when max quantity reached
            getBridgeSdk()
                .vibrate({ duration: 30, amplitude: VibrateAmplitudeTypes.LIGHT })
                .catch(() => void 0);
            // Trigger shake on parent component's limit text
            onMaxQuantityReached?.();
            return;
        }
        onQuantityChange(QTY_CHANGE_ACTION_TYPE.INCREMENT);
    }, [onQuantityChange, disableIncrement, onMaxQuantityReached]);

    const textVariant = useMemo(() => {
        return condensed ? "V2.body3.neutral.bold" : "V2.body1.neutral.bold";
    }, [condensed]);

    return (
        <div
            className={cx(styles.addButtonContainer, className, styles.addButtonContainerCartV2, {
                [styles.addButtonContainerPopCart]: isPopCart,
            })}
        >
            <button
                className={cx(styles.addButtonContainerAction, {
                    [styles.addButtonContainerActionCondensed]: condensed,
                    [styles.addButtonContainerActionPop]: isPopCart,
                })}
                onClick={onDecQuantity}
                aria-label={format(ACCESSIBLITY_TEXTS.MINUS_BUTTON, String(props.quantity - 1))}
                data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_DEC_BUTTON}
            >
                {condensed ? (
                    <MinusIcon />
                ) : (
                    <Text
                        variant={textVariant}
                        className={cx(styles.addButtonContainerActionMinus, {
                            [styles.addButtonContainerActionPopMinus]: isPopCart,
                        })}
                    >
                        --
                    </Text>
                )}
            </button>
            <AriaHidden
                as="div"
                className={cx(styles.addButtonContainerAction, styles.addButtonContainerActionQuantity, {
                    [styles.addButtonContainerActionCondensed]: condensed,
                    [styles.addButtonContainerActionQuantityCondensed]: condensed,
                    [styles.addButtonContainerActionPop]: isPopCart,
                })}
                data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_QUANTITY}
            >
                <Text variant={"V2.body3.neutral.bold"} color={"text_High_Emphasis"}>
                    {props.quantity}
                </Text>
            </AriaHidden>
            <button
                className={cx(styles.addButtonContainerAction, {
                    [styles.addButtonContainerActionCondensed]: condensed,
                    [styles.addButtonContainerActionPlus]: true,
                    [styles.addButtonContainerActionPop]: isPopCart,
                })}
                onClick={onIncQuantity}
                aria-label={format(ACCESSIBLITY_TEXTS.ADD_BUTTON, String(props.quantity + 1))}
                data-testid={CART_ITEMS_TEST_IDS.CART_ITEM_INC_BUTTON}
            >
                {condensed ? (
                    <PlusIcon isDisable={props.disableIncrement} />
                ) : (
                    <Text variant={textVariant} color={props.disableIncrement ? "text_disabled" : plusIconColor}>
                        +
                    </Text>
                )}
            </button>
            {props.updating && <div className={styles.addButtonContainerLoader} />}
        </div>
    );
});

export const AddButton = React.memo(function AddButton(props: AddButtonProps): ReactElement {
    const { onQuantityChange, quantity, disableIncrement, updating, customBorderColor, onMaxQuantityReached } = props;

    const onDecQuantity = React.useCallback(() => {
        onQuantityChange(QTY_CHANGE_ACTION_TYPE.DECREMENT);
    }, [onQuantityChange]);

    const onIncQuantity = React.useCallback(() => {
        if (disableIncrement) {
            // Haptic feedback
            getBridgeSdk()
                .vibrate({ duration: 50, amplitude: VibrateAmplitudeTypes.HEAVY })
                .catch(() => void 0);
            // Trigger shake on parent component's limit text
            onMaxQuantityReached?.();
            return;
        }

        onQuantityChange(QTY_CHANGE_ACTION_TYPE.INCREMENT);
    }, [onQuantityChange, disableIncrement, onMaxQuantityReached]);

    const addButtonVariant = useMemo(() => {
        if (isPopFlow()) {
            return ADD_BUTTON_VARIANT.POP;
        } else if (isToingFlow()) {
            return ADD_BUTTON_VARIANT.TOING;
        }
        return ADD_BUTTON_VARIANT.DEFAULT;
    }, []);

    const testIds = {
        quantityDataTestId: CART_ITEMS_TEST_IDS.CART_ITEM_QUANTITY,
        decrementCtaDataTestId: CART_ITEMS_TEST_IDS.CART_ITEM_DEC_BUTTON,
        incrementCtaDataTestId: CART_ITEMS_TEST_IDS.CART_ITEM_INC_BUTTON,
    };

    // Using DLS component for Cart V2
    return (
        <AddButtonV2
            value={quantity}
            onClickIncrement={onIncQuantity}
            onClickDecrement={onDecQuantity}
            testIds={testIds}
            isIncrementDisabled={disableIncrement}
            updating={updating}
            addButtonVariant={addButtonVariant}
            customQtyColor="text_High_Emphasis"
            size="xSmall"
            customBorderColor={customBorderColor}
        />
    );
});
